/**
 * Sibling products share product.metafields.custom.parent_title. Loads them via Storefront API
 * `search` (types: PRODUCT) with ProductFilter.productMetafield — not the Collections API.
 * Hydrates PDP option galleries and advanced product card colour rows after fetch.
 */
import { ThemeEvents } from '@theme/events';
import { CURRENCY_DECIMALS, DEFAULT_CURRENCY_DECIMALS, onDocumentReady } from '@theme/utilities';

const SIBLINGS_QUERY = `
query SiblingsByparentTitle($query: String!, $skuFilter: [ProductFilter!], $first: Int!, $after: String) {
  search(query: $query, types: PRODUCT, productFilters: $skuFilter, first: $first, after: $after) {
    edges {
      cursor
      node {
        __typename
        ... on Product {
          id
          handle
          title
          onlineStoreUrl
          mainTitle: metafield(namespace: "custom", key: "parent_title") {
            value
          }
          metafield(namespace: "custom", key: "badge") {
            value
          }
          featuredImage {
            url(transform: { maxWidth: 500 })
            altText
          }
          options(first: 5) {
            name
            optionValues {
              name
            }
          }
          availableForSale
          selectedOrFirstAvailableVariant {
            id
            availableForSale
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            image {
              url(transform: { maxWidth: 500 })
              altText
            }
            selectedOptions {
              name
              value
            }
          }
        }
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
`;

/** @type {WeakMap<Element, AbortController>} */
const galleryAbort = new WeakMap();

/** @type {WeakSet<Element>} */
const galleryHydrateStarted = new WeakSet();

/**
 * @param {string} gid
 * @returns {string}
 */
function gidToNumericId(gid) {
  const m = /Product\/(\d+)/.exec(gid);
  return m ? m[1] : '';
}

/**
 * @param {number} n
 * @param {string} templateOne
 * @param {string} templateOther
 */
function formatProductCardColourCount(n, templateOne, templateOther) {
  const one = (templateOne || '').trim();
  const other = (templateOther || '').trim();
  const lang = document.documentElement.lang || 'en';
  let category;
  try {
    category = new Intl.PluralRules(lang).select(n);
  } catch {
    category = n === 1 ? 'one' : 'other';
  }
  const tpl = category === 'one' && one ? one : other || one;
  if (!tpl) return String(n);
  return tpl.replace(/__ARI_COUNT__/g, String(n));
}

/**
 * @param {number} moneyValue - Amount in cents
 * @param {string} thousandsSeparator
 * @param {string} decimalSeparator
 * @param {number} precision
 */
function formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
  const roundedNumber = (moneyValue / 100).toFixed(precision);
  let [a, b] = roundedNumber.split('.');
  if (!a) a = '0';
  if (!b) b = '';
  a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);
  return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
}

/**
 * Storefront API amounts are major currency units; format with shop money_format (matches Liquid `| money`).
 * @param {string} amount
 * @param {string} currencyCode
 * @param {string} formatTemplate
 */
function formatMoneyFromShopAmount(amount, currencyCode, formatTemplate) {
  const n = Number(amount);
  if (Number.isNaN(n)) return '';
  const currency = currencyCode || '';
  const precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;
  const cents = Math.round(n * 10 ** precision);
  const template = formatTemplate || '{{amount}}';

  return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
    if (placeholder === 'currency') return currency;
    if (placeholder === 'amount_no_decimals') return formatCents(cents, ',', '.', 0);
    if (placeholder === 'amount_with_comma_separator') return formatCents(cents, '.', ',', precision);
    if (placeholder === 'amount_no_decimals_with_comma_separator') return formatCents(cents, '.', ',', 0);
    if (placeholder === 'amount_no_decimals_with_space_separator') return formatCents(cents, ' ', '.', 0);
    if (placeholder === 'amount_with_space_separator') return formatCents(cents, ' ', ',', precision);
    if (placeholder === 'amount_with_period_and_space_separator') return formatCents(cents, ' ', '.', precision);
    if (placeholder === 'amount_with_apostrophe_separator') return formatCents(cents, "'", '.', precision);
    return formatCents(cents, ',', '.', precision);
  });
}

/**
 * @param {Record<string, unknown>} node
 * @param {number} optionIndex
 * @returns {string}
 */
function optionValueAtIndex(node, optionIndex) {
  const options = /** @type {{ name: string }[]} */ (node.options || []);
  const optName = options[optionIndex]?.name;
  if (!optName) return '';
  const selected = /** @type {{ name: string; value: string }[]} */ (
    node.selectedOrFirstAvailableVariant?.selectedOptions || []
  );
  const found = selected.find((o) => o.name === optName);
  return found?.value || '';
}

/**
 * @param {Record<string, unknown>} node
 * @param {string} productUrl
 * @param {string} locale
 * @param {string} soldOutLabel
 * @param {string} saleLabel
 * @param {string} saleScheme
 * @param {string} soldOutScheme
 */
function thumbBadgeDataset(
  node,
  productUrl,
  locale,
  soldOutLabel,
  saleLabel,
  saleScheme,
  soldOutScheme,
  moneyFormat,
  currencyCode,
) {
  /** @type {string} */
  const shopMoneyFormat = moneyFormat || '{{amount}}';
  /** @type {string} */
  const shopCurrencyCode = currencyCode || '';
  const mf = /** @type {{ value?: string } | null} */ (node.metafield);
  let text = (mf?.value || '').trim();
  let scheme = saleScheme;
  /** @type {boolean} */
  let badgeFromSaleFallback = false;
  const v = node.selectedOrFirstAvailableVariant;
  const price = v?.price;
  const cap = v?.compareAtPrice;
  const productAvailableForSale = node.availableForSale === true;
  if (!text && !productAvailableForSale) {
    text = soldOutLabel;
    scheme = soldOutScheme;
  } else if (!text && price && cap) {
    const p = Number(price.amount);
    const c = Number(cap.amount);
    if (!Number.isNaN(p) && !Number.isNaN(c) && c > p) {
      text = saleLabel;
      scheme = saleScheme;
      badgeFromSaleFallback = true;
    }
  }
  const variantCurrency = String(v?.price?.currencyCode || shopCurrencyCode);
  const pricePlain = v?.price
    ? formatMoneyFromShopAmount(String(v.price.amount), variantCurrency, shopMoneyFormat)
    : '';
  const comparePlain =
    v?.compareAtPrice && v.price && Number(v.compareAtPrice.amount) > Number(v.price.amount)
      ? formatMoneyFromShopAmount(String(v.compareAtPrice.amount), variantCurrency, shopMoneyFormat)
      : '';
  const priceAmountStr = v?.price?.amount != null ? String(v.price.amount) : '';
  const compareAtAmountStr =
    v?.compareAtPrice?.amount != null && v?.price && Number(v.compareAtPrice.amount) > Number(v.price.amount)
      ? String(v.compareAtPrice.amount)
      : '';

  return {
    badgeText: text,
    badgeScheme: text ? scheme : '',
    badgeFromSaleFallback,
    priceAmount: priceAmountStr,
    compareAtAmount: compareAtAmountStr,
    pricePlain,
    compareAt: comparePlain,
    available: productAvailableForSale ? 'true' : 'false',
    imageUrl: v?.image?.url || node.featuredImage?.url || '',
    title: String(node.title || ''),
    productUrl,
    productId: gidToNumericId(String(node.id)),
  };
}

/**
 * @param {string} shopDomain
 * @param {string} token
 * @param {string} apiVersion
 * @param {string} parentTitle
 * @param {AbortSignal} signal
 */
async function fetchAllSiblings(shopDomain, token, apiVersion, parentTitle, signal) {
  const url = `https://${shopDomain}/api/${apiVersion}/graphql.json`;
  const skuFilter = [
    {
      productMetafield: {
        namespace: 'custom',
        key: 'parent_title',
        value: parentTitle,
      },
    },
  ];
  /** @type {Map<string, Record<string, unknown>>} */
  const byId = new Map();
  let after = null;
  let guard = 0;
  const maxPages = 40;

  while (guard < maxPages) {
    guard += 1;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({
        query: SIBLINGS_QUERY,
        variables: {
          query: parentTitle,
          skuFilter,
          first: 250,
          after,
        },
      }),
      signal,
    });
    if (!res.ok) throw new Error(`Storefront ${res.status}`);
    const json = await res.json();
    if (json.errors?.length) throw new Error(json.errors.map((e) => e.message).join('; '));

    const search = json.data?.search;
    const edges = search?.edges || [];
    for (const edge of edges) {
      const node = edge?.node;
      if (node?.__typename === 'Product' && node.id && node?.mainTitle?.value == parentTitle) {
        byId.set(String(node.id), node);
      }
    }
    const pageInfo = search?.pageInfo;
    if (!pageInfo?.hasNextPage || !pageInfo.endCursor) break;
    after = pageInfo.endCursor;
  }

  return [...byId.values()];
}

/**
 * @param {HTMLElement} gallery
 */
async function hydrateGallery(gallery) {
  if (galleryHydrateStarted.has(gallery)) return;

  const token = gallery.dataset.storefrontToken?.trim();
  const shopDomain = gallery.dataset.shopDomain?.trim();
  const apiVersion = gallery.dataset.apiVersion?.trim() || '2025-10';
  const parentTitle = gallery.dataset.parentTitle?.trim();
  const currentId = gallery.dataset.currentProductId?.trim();
  const locale = gallery.dataset.locale?.trim() || document.documentElement.lang || 'en';
  const soldOutLabel = gallery.dataset.badgeSoldOut || 'Sold out';
  const saleLabel = gallery.dataset.badgeSale || 'Sale';
  const saleScheme = gallery.dataset.badgeSaleScheme || '';
  const soldOutScheme = gallery.dataset.badgeSoldOutScheme || '';
  const moneyFormat = gallery.dataset.moneyFormat || '{{amount}}';
  const currencyCode = gallery.dataset.currency || '';

  if (!token || !shopDomain || !parentTitle) return;

  galleryHydrateStarted.add(gallery);

  galleryAbort.get(gallery)?.abort();
  const ac = new AbortController();
  galleryAbort.set(gallery, ac);

  let completed = false;
  try {
    const siblings = await fetchAllSiblings(shopDomain, token, apiVersion, parentTitle, ac.signal);

    function productUrlFor(node) {
      const u = node.onlineStoreUrl;
      if (u) return String(u);
      const h = String(node.handle || '').trim();
      return h ? (locale !== 'th' ? `/${locale}/products/${h}` : `/products/${h}`) : '#';
    }

    const colorSlots = [...gallery.querySelectorAll('[data-ari-siblings-fill="color"]')];
    for (const slot of colorSlots) {
      const idx = Number(slot.dataset.colorOptionIndex);
      if (Number.isNaN(idx)) continue;
      slot.replaceChildren();

      const sorted = [...siblings].sort((a, b) =>
        optionValueAtIndex(a, idx).localeCompare(optionValueAtIndex(b, idx), locale, { sensitivity: 'base' }),
      );

      for (const node of sorted) {
        const productUrl = productUrlFor(node);
        const thumbUrl = node.featuredImage?.url || node.selectedOrFirstAvailableVariant?.image?.url;
        const alt = node.featuredImage?.altText || node.title || '';
        const isCurrent = currentId && gidToNumericId(String(node.id)) === currentId;

        const link = document.createElement('ari-option-gallery-link');
        link.setAttribute('href', productUrl);
        link.className = `ari-option-gallery__thumb${isCurrent ? ' ari-option-gallery__thumb--current' : ''}`;
        link.setAttribute('aria-label', String(node.title || ''));
        link.setAttribute('tabindex', '0');

        const inner = document.createElement('span');
        inner.className = 'ari-option-gallery__thumb-inner';
        if (thumbUrl) {
          const img = document.createElement('img');
          img.className = 'ari-option-gallery__thumb-img';
          img.src = String(thumbUrl);
          img.alt = alt;
          img.loading = 'lazy';
          inner.appendChild(img);
        } else {
          inner.appendChild(document.createElement('span')).className = 'ari-option-gallery__thumb-placeholder';
        }
        link.appendChild(inner);
        slot.appendChild(link);
      }
    }

    const pillSlots = [...gallery.querySelectorAll('[data-ari-siblings-fill="pills"]')];
    for (const slot of pillSlots) {
      const optIdx = Number(slot.dataset.optionSlotIndex);
      if (Number.isNaN(optIdx)) continue;
      const currentVal = slot.dataset.currentValue || '';
      slot.replaceChildren();

      /** @type {Map<string, Record<string, unknown>>} */
      const firstByValue = new Map();
      for (const node of siblings) {
        if (gidToNumericId(String(node.id)) === currentId) continue;
        const val = optionValueAtIndex(node, optIdx);
        if (!val || firstByValue.has(val)) continue;
        firstByValue.set(val, node);
      }

      const sortedVals = [...firstByValue.keys()].sort((a, b) => a.localeCompare(b, locale, { sensitivity: 'base' }));
      for (const val of sortedVals) {
        const node = firstByValue.get(val);
        if (!node) continue;
        const productUrl = productUrlFor(node);
        const link = document.createElement('ari-option-gallery-link');
        link.setAttribute('href', productUrl);
        link.className = `ari-option-gallery__pill${val === currentVal ? ' ari-option-gallery__pill--current' : ''}`;
        link.setAttribute('aria-label', val);
        link.setAttribute('tabindex', '0');
        link.textContent = val;
        slot.appendChild(link);
      }
    }

    /** @type {HTMLElement | null} */
    const cardRoot = gallery.closest('product-card-advanced-option-gallery');
    if (cardRoot) {
      const limit = Number(cardRoot.dataset.thumbLimit) || 4;
      const colorIdx = Number(cardRoot.dataset.colorOptionIndex);
      const inner = cardRoot.querySelector('[data-ari-card-thumbs-inner]');
      if (inner && !Number.isNaN(colorIdx)) {
        inner.replaceChildren();
        const sorted = [...siblings].sort((a, b) =>
          optionValueAtIndex(a, colorIdx).localeCompare(optionValueAtIndex(b, colorIdx), locale, {
            sensitivity: 'base',
          }),
        );
        let n = 0;
        for (const node of sorted) {
          if (n >= limit) break;
          n += 1;
          const productUrl = productUrlFor(node);
          const thumbDisplay = node.featuredImage?.url || node.selectedOrFirstAvailableVariant?.image?.url;
          const badge = thumbBadgeDataset(
            node,
            productUrl,
            locale,
            soldOutLabel,
            saleLabel,
            saleScheme,
            soldOutScheme,
            moneyFormat,
            currencyCode,
          );
          const isCurrent = currentId && gidToNumericId(String(node.id)) === currentId;

          const span = document.createElement('span');
          span.className = `ari-option-gallery__thumb product-card-advanced-option-thumb${isCurrent ? ' ari-option-gallery__thumb--current' : ''}`;
          span.setAttribute('role', 'button');
          span.setAttribute('tabindex', '0');
          span.setAttribute('aria-label', badge.title);
          span.dataset.productId = badge.productId;
          span.dataset.productUrl = productUrl;
          span.dataset.title = badge.title;
          span.dataset.imageUrl = badge.imageUrl;
          span.dataset.price = badge.pricePlain;
          span.dataset.compareAtPrice = badge.compareAt;
          span.dataset.available = badge.available;
          span.dataset.badgeText = badge.badgeText;
          span.dataset.badgeScheme = badge.badgeScheme;
          span.dataset.badgeFromSaleFallback = badge.badgeFromSaleFallback ? 'true' : 'false';
          span.dataset.priceAmount = badge.priceAmount;
          span.dataset.compareAtAmount = badge.compareAtAmount;

          const tin = document.createElement('span');
          tin.className = 'ari-option-gallery__thumb-inner';
          if (thumbDisplay) {
            const img = document.createElement('img');
            img.className = 'ari-option-gallery__thumb-img';
            img.src = String(thumbDisplay);
            img.alt = node.featuredImage?.altText || node.title || '';
            img.loading = 'lazy';
            tin.appendChild(img);
          } else {
            tin.appendChild(document.createElement('span')).className = 'ari-option-gallery__thumb-placeholder';
          }
          span.appendChild(tin);
          inner.appendChild(span);
        }
      }

      const cardContent = cardRoot.closest('[data-product-card-advanced-content]');
      const countLabel = cardContent?.querySelector('[data-ari-colour-count-label]');
      const countBtn = countLabel?.closest('[data-product-card-advanced-color-toggle]');
      if (countLabel && countBtn && !Number.isNaN(colorIdx)) {
        const uniqueColors = new Set(siblings.map((s) => optionValueAtIndex(s, colorIdx)).filter(Boolean));
        const n = uniqueColors.size;
        const templateOne = countBtn.getAttribute('data-ari-colour-count-template-one') || '';
        const templateOther = countBtn.getAttribute('data-ari-colour-count-template-other') || '';
        countLabel.textContent = formatProductCardColourCount(n, templateOne, templateOther);
      }
      cardRoot.dispatchEvent(new CustomEvent('ari-siblings-loaded', { bubbles: true }));
    }

    completed = true;
  } catch (e) {
    if (e.name !== 'AbortError') {
      console.error('[parent-title-siblings]', e);
      gallery.querySelectorAll('[data-ari-siblings-fill]').forEach((el) => {
        el.replaceChildren();
      });
    }
  } finally {
    if (!completed) {
      galleryHydrateStarted.delete(gallery);
      galleryAbort.delete(gallery);
    }
  }
}

function initAriparentTitleSiblings() {
  document.querySelectorAll('[data-ari-parent-title-gallery]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (el.dataset.ariSkipHydrate === 'true') return;
    hydrateGallery(el);
  });
}

/**
 * After variant-picker morphs, Liquid `data-current-product-id` on the gallery is stale for
 * combined listings. Resolve the picker scoped to this gallery and mirror its product id.
 * @param {HTMLElement} gallery
 */
function syncGalleryCurrentProductIdFromVariantPicker(gallery) {
  let /** @type {Element | null | undefined} */ picker =
    gallery.closest('form[action*="/cart/add"]')?.querySelector('variant-picker[data-product-id]') ?? null;

  if (!picker) {
    const host = gallery.closest('.product-card-advanced, product-card');
    picker = host?.querySelector('variant-picker[data-product-id]') ?? null;
  }

  if (!picker) {
    const section = gallery.closest('.shopify-section');
    const pickers = section?.querySelectorAll('variant-picker[data-product-id]');
    if (pickers?.length === 1) picker = pickers[0];
  }

  const pid = picker instanceof HTMLElement ? picker.dataset.productId?.trim() : '';
  if (pid) gallery.dataset.currentProductId = pid;
}

/**
 * Variant picker fetch returns fresh option labels; only the picker is morphed so the gallery
 * label span stays stale until we copy from the response document.
 * @param {Document} htmlDoc
 */
function syncOptionGalleryLabelFromFetchedHtml(htmlDoc) {
  document.querySelectorAll('[data-ari-parent-title-gallery].ari-option-gallery').forEach((live) => {
    if (!(live instanceof HTMLElement)) return;
    const sid = live.dataset.sectionId;
    const bid = live.dataset.blockId;
    if (!sid || !bid) return;

    let fresh;
    try {
      fresh = htmlDoc.querySelector(
        `[data-ari-parent-title-gallery].ari-option-gallery[data-section-id="${CSS.escape(sid)}"][data-block-id="${CSS.escape(bid)}"]`,
      );
    } catch {
      return;
    }

    const liveLabel = live.querySelector('.ari-option-gallery__label-value');
    const freshLabel = fresh?.querySelector('.ari-option-gallery__label-value');
    if (liveLabel && freshLabel) liveLabel.textContent = freshLabel.textContent;
  });
}

/**
 * @param {HTMLElement} gallery
 */
function resetGalleryHydrationState(gallery) {
  galleryHydrateStarted.delete(gallery);
  galleryAbort.get(gallery)?.abort();
  galleryAbort.delete(gallery);
}

function refreshAriParentTitleGalleriesAfterVariantDomUpdate() {
  document.querySelectorAll('[data-ari-parent-title-gallery]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (el.dataset.ariSkipHydrate === 'true') return;
    syncGalleryCurrentProductIdFromVariantPicker(el);
    resetGalleryHydrationState(el);
    hydrateGallery(el);
  });
}

document.addEventListener('ari-request-parent-title-hydrate', (e) => {
  const el = e.target;
  if (!(el instanceof HTMLElement) || !el.matches('[data-ari-parent-title-gallery]')) return;
  if (el.dataset.ariSkipHydrate === 'true') return;
  hydrateGallery(el);
});

window.addEventListener('ari-parent-title-gallery-disconnected', (e) => {
  const g = e.detail?.gallery;
  if (g instanceof HTMLElement) resetGalleryHydrationState(g);
});

onDocumentReady(() => {
  initAriparentTitleSiblings();
});

document.addEventListener(ThemeEvents.variantUpdate, (e) => {
  const html = e.detail?.data?.html;
  if (html instanceof Document) {
    syncOptionGalleryLabelFromFetchedHtml(html);
  }
});

document.addEventListener(ThemeEvents.variantDomUpdated, () => {
  requestAnimationFrame(() => refreshAriParentTitleGalleriesAfterVariantDomUpdate());
});
